import { createQueryKeys, type Query } from '@shared/api';

/**
 * Ключи запросов оргтехники (ADR 0085).
 *
 * Видов запроса два, и разводить их обязательно. Страница вкладки: объект, отдел, тип, гарантия и
 * поиск приходят в параметрах. Выдача для выбора техники в заявке на обслуживание (§9.3 плана).
 * Под общим ключом форма получила бы отфильтрованную страницу таблицы, а таблица — весь справочник
 * мимо своих фильтров: кто спросил вторым, тот и определил бы ответ обоим.
 *
 * Третьей выдачей рядом с ними встал селектор предмета заявки (`selector*` ниже): он отвечает по
 * ВСЕМУ активному парку, а не по области, и потому не имеет права делить запись кэша ни с первой,
 * ни со второй.
 *
 * Жило здесь и семейство `warranties` — сроки гарантий всего парка для колонки списка заявок.
 * Оно снято вместе со своим запросом (Ф3 плана кандидата): срок гарантии единицы приезжает теперь
 * в самой строке заявки, и спрашивать у справочника колонке больше нечего.
 */
export const officeEquipmentKeys = createQueryKeys('office-equipment', {
  list: (params: Query) => ['list', params],
  /**
   * Выдача для поля «Какой аппарат» — **срезом по набранному** (план кандидата, Ф1): отбор ушёл на
   * сервер, и набранное стало частью вопроса, а не способом порезать уже полученное. Тем же
   * ключом снимается и вопрос о задержке ввода: каждая выдача лежит своей записью, и повторный
   * набор того же ответа с сервера не просит — отдельный debounce был бы вторым, менее точным
   * механизмом поверх этого.
   *
   * Набранное нормализуется: пустая строка и «не искали» — один вопрос, и разными записями кэша
   * они означали бы два одинаковых запроса подряд на каждом закрытии списка.
   */
  options: (search?: string) => ['options', search?.trim() || null],
  /**
   * Карточка по идентификатору: только она приносит историю обслуживания и гарантии ремонтов
   * (§8.2) — в списке этого среза нет, и держать её под ключом списка значило бы либо потерять
   * секцию, либо тащить подзапрос на каждую строку справочника.
   */
  detail: (id: string) => ['detail', id],
  /**
   * ВЫБОР ПРЕДМЕТА ЗАЯВКИ — своя пара семейств (план
   * `docs/office-equipment-request-subject-plan.md`, Р1), а не общие `options` и `detail`.
   *
   * Развести их обязательно, и это находка ревью. Проекция селектора отвечает по ВСЕМУ активному
   * парку — от трёх набранных символов, — а обычные список и карточка сужены областью учётки.
   * Дочитка выбранного под ключом `detail` положила бы запись вне области в ту же ячейку, которой
   * живёт обычная карточка: следующий, кто открыл бы аппарат из справочника, получил бы её из кэша,
   * и виновата была бы не ручка, а react-query. Пересечься семействам теперь нечем — у них разные
   * сегменты и разные ручки.
   *
   * Корень при этом общий, и это тоже намеренно: заведённая или изменённая карточка обязана
   * доехать до обеих выдач, а гасят их по корню сущности.
   *
   * Набранное нормализуется так же, как в `options`: пустая строка и «не искали» — один вопрос.
   */
  selectorOptions: (search?: string) => ['selector', 'options', search?.trim() || null],
  selectorPicked: (id: string) => ['selector', 'detail', id],
  /**
   * Лента карточки (Р62): перемещения и обслуживание. Своё семейство, а не часть карточки — она
   * приходит отдельной ручкой и устаревает в другие моменты: заявка сменила статус, единицу
   * перевезли.
   */
  history: (id: string) => ['history', id],
  /**
   * Три бизнес-блока той же истории (план `docs/office-equipment-history-blocks-plan.md`, Р1):
   * «Связанные заявки», «Ручные правки», «Перемещения». Тремя семействами, а не одним с видом
   * блока в параметрах: у них разные ручки, разные курсоры и разное право — заявки просят ещё и
   * `serviceRequests.read`, — и общий ключ связал бы отказ одной вкладки с содержимым двух
   * соседних, которые смотрящему открыты.
   *
   * РАЗМЕР СТРАНИЦЫ — ЧАСТЬ КЛЮЧА, и это не педантизм. У блока заявок два читателя с разной
   * обрезкой: вкладка окна (страница целиком) и секция карточки — первые пять строк (Р7). Под
   * общим ключом ответ обоим определил бы тот, кто спросил первым: секция показала бы двадцать
   * строк, а вкладка — пять и кнопку «Показать ещё», за которой ничего нет.
   */
  requests: (id: string, pageSize: number) => ['requests', id, pageSize],
  changes: (id: string, pageSize: number) => ['changes', id, pageSize],
  movements: (id: string, pageSize: number) => ['movements', id, pageSize],
});

/**
 * The type modal and form selectors share one complete list. Disabled types stay present so an
 * existing equipment card retains its label while the selector marks the type unavailable.
 */
export const officeEquipmentTypeKeys = createQueryKeys('office-equipment-types', {
  options: () => ['options'],
});

/**
 * Ключи справочника моделей аппаратов (план `docs/office-equipment-consumables-plan.md`, Р14).
 *
 * Свой корень, а не семейство внутри `office-equipment`: у моделей своя ручка, своё окно и своя
 * жизнь — их ведут отдельно от парка. Общий корень означал бы, что каждая заведённая карточка
 * техники перезапрашивает весь перечень моделей, а правка модели — гасит страницу справочника
 * заодно с ним.
 *
 * Обратное при этом верно и обязательно (матрица Р14): правка, заведение и гашение модели гасят и
 * `office-equipment` — имя карточки с выпуска A зеркалит модель, и переименование обязано доехать
 * до строк справочника. Это делает тот, кто пишет, — здесь только ключи.
 *
 * Семейства два. Список окна: тип, активность, поиск и страница приходят в параметрах — и
 * неактивные модели там нужны не меньше активных, их в окне и включают обратно. И перечень для
 * выпадающего списка формы карточки техники — **по типу**: одноимённые принтер и МФУ это разные
 * модели (Р1), и общий ключ на все типы подсунул бы форме перечень чужого аппарата.
 */
export const officeEquipmentModelKeys = createQueryKeys('office-equipment-models', {
  list: (params: Query) => ['list', params],
  options: (equipmentTypeId: string | undefined) => ['options', equipmentTypeId ?? null],
  /**
   * Характеристики типа со значениями на выбор (план `docs/office-equipment-specs-plan.md`).
   * Семейство моделей, а не техники: перечень меняется вместе со справочником моделей и нужен
   * ровно там, где модель заводят. Меняется он только миграцией, поэтому и держится долго.
   */
  specs: (equipmentTypeId: string | undefined) => ['specs', equipmentTypeId ?? null],
});

/**
 * Ключи справочника расходников — картриджей и тонеров (план
 * `docs/office-equipment-consumables-plan.md`, Р14).
 *
 * Свой корень, а не семейство внутри `office-equipment`: у расходников своя ручка, своё окно и
 * своя жизнь — их ведут отдельно от парка, и заведённая карточка техники перечень картриджей не
 * меняет.
 *
 * Обратное неверно и потому прописано матрицей Р14: правка расходника, его остатка и привязки к
 * моделям гасят и `office-equipment` — карточка единицы отвечает «чем заправлять» и показывает
 * остаток (Р15), то есть держит чужие данные у себя в ответе. Гасит тот, кто пишет; здесь только
 * ключи.
 *
 * The registry list follows its page and filters. Pickers use a separate family so a full
 * active set cannot be overwritten by a filtered registry page. The detail and stock history
 * remain independent responses.
 */
export const officeEquipmentConsumableKeys = createQueryKeys('office-equipment-consumables', {
  list: (params: Query) => ['list', params],
  picker: ({ activeOnly, modelId }: { activeOnly: boolean; modelId?: string }) => [
    'picker',
    { activeOnly, modelId: modelId ?? null },
  ],
  detail: (id: string) => ['detail', id],
  /**
   * Журнал остатка позиции страницами (план `docs/office-equipment-consumables-and-purchase-plan.md`,
   * Р4). Своё семейство, а не часть карточки, и это прямое следствие того, что лента уехала из
   * `GET /:id` в свою ручку: под ключом карточки страница и отбор по виду события стали бы частью
   * ключа записи — то есть каждое перелистывание журнала объявляло бы устаревшей саму позицию.
   *
   * Идентификатор стоит ПЕРЕД параметрами: журнал всегда про одну позицию (общего нет вовсе), и
   * такой порядок сегментов позволяет погасить ленту ровно одной карточки, не трогая соседние.
   */
  stockEntries: (id: string, params: Query) => ['stock-entries', id, params],
  /**
   * Отчёт по расходу за период (Р10): своё семейство с периодом в параметрах. Под ключом списка
   * он жил бы вместе с перечнем позиций — и правка остатка одной карточки перезапрашивала бы
   * отчёт за прошлый квартал, который от неё не меняется.
   */
  usage: (params: Query) => ['usage', params],
});

/**
 * Ключи плановых закупок расходников (план
 * `docs/office-equipment-consumables-and-purchase-plan.md`, Р9, Р15).
 *
 * СВОЙ КОРЕНЬ, потому что закупка — самостоятельный документ, а не срез справочника: у неё свои
 * ручки, свой цикл и своя видимость по праву `officeEquipmentPurchases.manage`. Класть её внутрь
 * `office-equipment-consumables` значило бы гасить перечень позиций на каждый переход статуса
 * закупки — и наоборот, перечитывать закупки от каждой правки комментария в карточке картриджа.
 *
 * СВЯЗЬ ПРИ ЭТОМ ДВУСТОРОННЯЯ, И ОБЕ СТОРОНЫ ОБЯЗАТЕЛЬНЫ (Р15). Заведение, проведение, закрытие и
 * отмена закупки двигают «уже заказано» и дефицит у позиций — значит гасят и корень расходников.
 * Правка потребности или остатка меняет предзаполнение — значит гасит и корень закупок. Гасит тот,
 * кто пишет; здесь только ключи.
 *
 * Семейства три. Список: состояние, порядок и страница приходят в параметрах. Карточка по
 * идентификатору — шапка, лента переходов и строки со снимком расчёта. И предзаполнение формы
 * своим семейством без параметров: его считает сервер на момент запроса, и держать его под ключом
 * списка значило бы отдавать форме то, что посчитано до последней выдачи со склада.
 */
export const officeEquipmentPurchaseKeys = createQueryKeys('office-equipment-purchases', {
  list: (params: Query) => ['list', params],
  detail: (id: string) => ['detail', id],
  prefill: () => ['prefill'],
});
